import { useMemo, useState } from "react";
import { PUNTAJE_APRUEBA } from "@huayruro/shared";
import { mutar, useApi } from "../lib/useApi";
import { navegar } from "../lib/ruta";
import { ahoraFecha } from "../lib/fecha-ui";
import type { SesionActiva } from "../lib/tipos";
import { MiContrasenaModal } from "../components/MiContrasenaModal";
import { EVENTO_MI_CUENTA } from "../components/Layout";
import { Button, Chip, EmptyState, Input, cn, useToast } from "../components/ui";

// «Mi cuenta» de la vendedora (brief 26-oct-2026, paso 2): SU página, no el panel. «Mi aprendizaje» vive
// aquí: el examen del día, el plan del día, sus notas y las fichas aprobadas. Patrón del research
// (docs/research/mi-aprendizaje): lo de HOY primero; en escritorio dos columnas; en el celular una sola en
// el orden examen → plan → notas → fichas → cuenta. Sin puntos, insignias ni ranking entre compañeras.

type EstadoExamen =
  | { estado: "sin_carga" }
  | { estado: "pendiente"; productos: number }
  | { estado: "en_curso" | "terminado"; examen: { id: string; vence_at: string; puntaje: number | null; puntaje_max: number; aprobado: boolean | null } };
type Tarea = { id: string; texto: string; hecha_at: string | null };
type Nota = { id: string; fecha: string; puntaje: number; puntaje_max: number; aprobado: boolean; falladas: { enunciado: string; respuesta: string | null; correcta: string }[] };
export type Ficha = {
  id: string; nombre_mostrar: string; principio_activo: string; para_que_sirve: string; indicaciones: string; posologia: string;
  efectos_secundarios: string; contraindicaciones: string; suele_pedir_receta: number; cuidados_al_entregar: string;
  cuando_llamar_al_quimico: string; fuente: string; cargaste?: boolean;
};

const avisarCabecera = () => window.dispatchEvent(new Event(EVENTO_MI_CUENTA));
const fechaCorta = (ymd: string) => `${ymd.slice(8, 10)}/${ymd.slice(5, 7)}`;

export function MiCuenta({ sesion, onSalir }: { sesion: SesionActiva; onSalir: () => void }) {
  const [clave, setClave] = useState(false);
  const toast = useToast();
  return (
    <div className="mx-auto flex w-full max-w-[1100px] flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="outline" onClick={() => navegar("mostrador")} className="min-h-[44px]">
          ← Caja
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-[20px] font-bold leading-tight text-ink">Mi cuenta</h1>
          <p className="truncate text-[12.5px] text-ink-2">
            {sesion.usuario.nombre} · {sesion.sucursal?.nombre ?? "Toda la cadena"}
          </p>
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <div className="flex flex-col gap-4">
          <TarjetaExamen />
          <PlanDelDia />
          <MisNotas />
        </div>
        <div className="flex flex-col gap-4">
          <Fichas />
          <section className="rounded-[14px] border border-line bg-card p-4">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.09em] text-ink-3">Tu cuenta</h2>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => setClave(true)} className="min-h-[44px]">
                Mi contraseña
              </Button>
              <Button variant="outline" onClick={onSalir} className="min-h-[44px]">
                Cerrar sesión
              </Button>
            </div>
          </section>
        </div>
      </div>
      {clave && (
        <MiContrasenaModal
          onCerrar={() => setClave(false)}
          onListo={() => {
            toast("Tu contraseña quedó cambiada.");
            setClave(false);
          }}
        />
      )}
    </div>
  );
}

// ---------------- Examen de hoy ----------------
function TarjetaExamen() {
  const { data, cargando } = useApi<EstadoExamen>("/aprendizaje/examen/hoy");
  const base = "rounded-[14px] border p-4 sm:p-5";
  if (cargando || !data) return <section className={cn(base, "border-line bg-card text-[13px] text-ink-3")}>Cargando el examen de hoy…</section>;
  if (data.estado === "sin_carga") {
    return (
      <section className={cn(base, "border-line bg-card")} aria-label="Examen de hoy">
        <h2 className="text-[16px] font-bold text-ink">Hoy no hay examen todavía</h2>
        <p className="mt-1 text-[13px] leading-snug text-ink-2">El examen sale con los productos que tú cargas. Sube tu primera repisa en Inventario › Ingreso y aquí aparece.</p>
        <Button variant="outline" onClick={() => navegar("ingreso")} className="mt-3 min-h-[44px]">
          Ir a Ingreso
        </Button>
      </section>
    );
  }
  if (data.estado === "pendiente") {
    return (
      <section className={cn(base, "border-accent bg-accent-soft")} aria-label="Examen de hoy">
        <p className="text-[12px] font-bold uppercase tracking-[0.08em] text-accent-ink">Examen pendiente</p>
        <h2 className="mt-1 text-[18px] font-bold text-ink">10 preguntas · 10 minutos</h2>
        <p className="mt-1 text-[13px] leading-snug text-ink-2">Sobre los {data.productos} productos que cargaste hoy. Apruebas con {PUNTAJE_APRUEBA} de 12. Las respuestas se ven al final.</p>
        <Button onClick={() => navegar("examen")} className="mt-3 min-h-[48px] text-[15px]">
          Empezar el examen
        </Button>
      </section>
    );
  }
  const ex = data.examen;
  if (data.estado === "en_curso") {
    return (
      <section className={cn(base, "border-accent bg-accent-soft")} aria-label="Examen de hoy">
        <h2 className="text-[17px] font-bold text-ink">Tienes el examen a medias</h2>
        <p className="mt-1 text-[13px] text-ink-2">Se cierra solo a las {new Date(ex.vence_at).toLocaleTimeString("es-PE", { hour: "2-digit", minute: "2-digit", hour12: false })}.</p>
        <Button onClick={() => navegar("examen")} className="mt-3 min-h-[48px] text-[15px]">
          Seguir el examen
        </Button>
      </section>
    );
  }
  return (
    <section className={cn(base, ex.aprobado ? "border-ok bg-ok-soft" : "border-line bg-card")} aria-label="Examen de hoy">
      <p className="text-[12px] font-bold uppercase tracking-[0.08em] text-ink-3">Examen de hoy</p>
      <p className="mt-1 flex items-baseline gap-2">
        <span className="text-[32px] font-bold leading-none tabular-nums text-ink">
          {ex.puntaje}/{ex.puntaje_max}
        </span>
        <Chip variant={ex.aprobado ? "ok" : "warn"}>{ex.aprobado ? `Aprobado · mínimo ${PUNTAJE_APRUEBA}` : `No aprobado · mínimo ${PUNTAJE_APRUEBA}`}</Chip>
      </p>
      <Button variant="outline" onClick={() => navegar("examen")} className="mt-3 min-h-[44px]">
        Ver mis respuestas
      </Button>
    </section>
  );
}

// ---------------- Plan del día ----------------
function PlanDelDia() {
  const { data, recargar } = useApi<{ tareas: Tarea[] }>("/aprendizaje/plan");
  const [enviando, setEnviando] = useState<string | null>(null);
  const tareas = data?.tareas ?? [];
  const hechas = tareas.filter((t) => t.hecha_at).length;
  async function marcar(t: Tarea) {
    setEnviando(t.id);
    try {
      await mutar(`/aprendizaje/tareas/${t.id}/hecha`, { method: "POST", body: { hecha: !t.hecha_at } });
      recargar();
      avisarCabecera();
    } finally {
      setEnviando(null);
    }
  }
  return (
    <section className="rounded-[14px] border border-line bg-card p-4" aria-label="Plan del día">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-[16px] font-bold text-ink">Plan del día</h2>
        {tareas.length > 0 && (
          <span className="text-[12.5px] text-ink-2">
            {hechas} de {tareas.length} hechas
          </span>
        )}
      </div>
      {data && tareas.length === 0 && <p className="mt-2 text-[13px] text-ink-2">Hoy no te programaron tareas. Aparecen aquí cuando el Encargado o el Dueño las arman.</p>}
      <ul className="mt-2 flex flex-col gap-1.5">
        {tareas.map((t) => (
          <li key={t.id}>
            <label className={cn("flex min-h-[48px] cursor-pointer items-center gap-3 rounded-[10px] border px-3 py-2", t.hecha_at ? "border-ok bg-ok-soft" : "border-line-input bg-card")}>
              <input type="checkbox" className="h-5 w-5 flex-none accent-ok" checked={!!t.hecha_at} disabled={enviando === t.id} onChange={() => void marcar(t)} />
              <span className={cn("text-[14px] text-ink", t.hecha_at && "text-ink-2 line-through")}>{t.texto}</span>
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
}

// ---------------- Mis notas: la semana con la línea del 10 ----------------
function MisNotas() {
  const { data } = useApi<{ notas: Nota[] }>("/aprendizaje/mis-notas");
  const notas = useMemo(() => data?.notas ?? [], [data]);
  // Siempre los últimos 7 días (con «—» el día que no rindió), igual que la curva del panel.
  const ultimas = useMemo(() => {
    const porDia = new Map(notas.map((n) => [n.fecha, n]));
    return Array.from({ length: 7 }, (_, k) => ahoraFecha(k - 6)).map((d) => ({ fecha: d, n: porDia.get(d) ?? null }));
  }, [notas]);
  return (
    <section className="rounded-[14px] border border-line bg-card p-4" aria-label="Mis notas">
      <h2 className="text-[16px] font-bold text-ink">Mis notas</h2>
      {data && notas.length === 0 && <p className="mt-2 text-[13px] text-ink-2">Todavía no diste ningún examen. Tu primera nota aparece aquí.</p>}
      {notas.length > 0 && <CurvaNotas puntos={ultimas.map(({ fecha, n }) => ({ etiqueta: fechaCorta(fecha), valor: n?.puntaje ?? null, maximo: n?.puntaje_max ?? 12 }))} />}
      {notas.length > 0 && (
        <ul className="mt-2 flex flex-col">
          {notas.slice(0, 7).map((n) => (
            <li key={n.id} className="flex items-center justify-between gap-2 border-t border-line-row py-2 text-[13px]">
              <span className="text-ink">{fechaCorta(n.fecha)}</span>
              <span className="tabular-nums text-ink">
                {n.puntaje}/{n.puntaje_max}
              </span>
              <Chip variant={n.aprobado ? "ok" : "warn"}>{n.aprobado ? "Aprobado" : `Falló ${n.falladas.length}`}</Chip>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Barras por día con la línea del aprobado (10 de 12). Cada barra lleva su número escrito. */
export function CurvaNotas({ puntos, alto = 120 }: { puntos: { etiqueta: string; valor: number | null; maximo: number }[]; alto?: number }) {
  const ancho = 320;
  const paso = ancho / Math.max(puntos.length, 1);
  const y = (v: number, max: number) => alto - 18 - (v / Math.max(max, 1)) * (alto - 34);
  const yLinea = y(PUNTAJE_APRUEBA, 12);
  return (
    <svg viewBox={`0 0 ${ancho} ${alto}`} role="img" aria-label={`Notas: ${puntos.map((p) => `${p.etiqueta} ${p.valor ?? "no rindió"}`).join(", ")}`} className="mt-3 h-auto w-full max-w-[420px]">
      <line x1={0} x2={ancho} y1={yLinea} y2={yLinea} className="stroke-ok" strokeDasharray="4 3" strokeWidth={1} />
      <line x1={2} x2={16} y1={7} y2={7} className="stroke-ok" strokeDasharray="4 3" strokeWidth={1} />
      <text x={20} y={10} className="fill-ok text-[9px]">
        aprueba con {PUNTAJE_APRUEBA}
      </text>
      {puntos.map((p, i) => {
        // Barra de ancho tope: con un solo día no se vuelve un bloque que ocupa todo.
        const w = Math.min(paso * 0.5, 26);
        const x = i * paso + (paso - w) / 2;
        if (p.valor === null)
          return (
            <text key={i} x={x + w / 2} y={alto - 22} textAnchor="middle" className="fill-ink-3 text-[9px]">
              —
            </text>
          );
        const top = y(p.valor, p.maximo);
        const ok = p.valor * 12 >= PUNTAJE_APRUEBA * p.maximo;
        return (
          <g key={i}>
            <rect x={x} y={top} width={w} height={alto - 18 - top} rx={3} className={ok ? "fill-ok" : "fill-warn"} />
            <text x={x + w / 2} y={top - 3} textAnchor="middle" className="fill-ink text-[10px] font-semibold">
              {p.valor}
            </text>
            <text x={x + w / 2} y={alto - 4} textAnchor="middle" className="fill-ink-2 text-[9px]">
              {p.etiqueta}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

// ---------------- Fichas aprobadas ----------------
function Fichas() {
  const { data } = useApi<{ fichas: Ficha[] }>("/aprendizaje/fichas");
  const [q, setQ] = useState("");
  const [abierta, setAbierta] = useState<string | null>(null);
  const fichas = (data?.fichas ?? []).filter((f) => !q.trim() || `${f.nombre_mostrar} ${f.para_que_sirve}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <section className="rounded-[14px] border border-line bg-card p-4" aria-label="Fichas de medicamentos">
      <h2 className="text-[16px] font-bold text-ink">Fichas para estudiar</h2>
      <p className="mt-0.5 text-[12.5px] text-ink-2">Primero las de lo que cargaste esta semana. Las aprobó el Dueño.</p>
      {data && data.fichas.length === 0 ? (
        <EmptyState className="mt-3" title="Todavía no hay fichas aprobadas" subtitle="Aparecen aquí apenas el Dueño las apruebe." />
      ) : (
        <>
          <Input aria-label="Buscar ficha" placeholder="Buscar: amoxicilina, fiebre, alergia…" value={q} onChange={(e) => setQ(e.target.value)} className="mt-3 min-h-[44px] text-[14px]" />
          <ul className="mt-2 flex max-h-[640px] flex-col gap-1.5 overflow-y-auto">
            {fichas.map((f) => (
              <li key={f.id} className="rounded-[10px] border border-line-input">
                <button type="button" onClick={() => setAbierta(abierta === f.id ? null : f.id)} aria-expanded={abierta === f.id} className="flex min-h-[52px] w-full items-center gap-2 px-3 py-2 text-left">
                  <span className="min-w-0 flex-1">
                    <span className="block text-[14px] font-semibold text-ink">
                      {f.nombre_mostrar}
                      {f.cargaste && <span className="ml-2 align-middle text-[11px] font-semibold text-ok">la cargaste</span>}
                    </span>
                    <span className="block text-[12.5px] text-ink-2">{f.para_que_sirve}</span>
                  </span>
                  {f.suele_pedir_receta === 1 && <Chip variant="danger">Receta</Chip>}
                </button>
                {abierta === f.id && <FichaDetalle f={f} />}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

export function FichaDetalle({ f }: { f: Ficha }) {
  const bloques: [string, string][] = [
    ["Para qué sirve", f.indicaciones],
    ["Cómo se toma", f.posologia],
    ["Efectos que puede dar", f.efectos_secundarios],
    ["Quién no debe tomarlo", f.contraindicaciones],
    ["Al entregar", f.cuidados_al_entregar],
    ["Llama al químico si…", f.cuando_llamar_al_quimico],
  ];
  return (
    <div className="flex flex-col gap-2 border-t border-line-row px-3 pb-3 pt-2">
      {bloques.map(([t, x]) => (
        <div key={t}>
          <h3 className={cn("text-[12px] font-bold uppercase tracking-[0.06em]", t.startsWith("Llama") ? "text-accent-ink" : "text-ink-3")}>{t}</h3>
          <p className="text-[13.5px] leading-snug text-ink">{x}</p>
        </div>
      ))}
      <p className="text-[11.5px] text-ink-3">Fuente: {f.fuente}</p>
    </div>
  );
}
